import { pickPhoto } from '@/services/photo-check/merge-photos';
import { getProtectedAxiosInstance } from '@/services/axios/Axios';
import {
  PhotoCheckProblem,
  PhotoGeofenceResult,
  PhotoLocation
} from '@/types/photo-check.type';
import { create } from 'zustand';

export interface PhotoCheckItem {
  id: string;
  checked_at: string;
  taken_at: string | null;
  media_type: string;
  photo: string | null;
  run_name: string | null;
  taken_by: string | null;
  status: string;
  location?: PhotoLocation | null;
  geofence?: PhotoGeofenceResult | null;
  tasks: {
    task_id?: string;
    title: string | null;
    suggestion: 'done' | 'attention' | 'review';
    score?: number | null;
    reason: string | null;
    problem?: PhotoCheckProblem | null;
  }[];
}

interface RunPhotosState {
  runId?: string;
  // Newest photo of each task in the opened run, by builder task id
  byTask: { [taskId: string]: PhotoCheckItem };
  load: (runId: string) => void;
}

/** Photos of one run, shown next to each task when the creator opens the run from Analytics */
export const useRunPhotosStore = create<RunPhotosState>((set, get) => ({
  runId: undefined,
  byTask: {},
  load: (runId: string) => {
    // Load again on every open, so new photos and fresh links show
    set({ runId, byTask: {} });
    getProtectedAxiosInstance()
      .post('/run/photo-check/list', { run_id: runId })
      .then(r => {
        if (get().runId !== runId) return;
        const items: PhotoCheckItem[] = r.data?.data?.items || [];
        const byTask: { [taskId: string]: PhotoCheckItem } = {};
        // Each photo is checked against every task. Show next to a task the photo that decided it, by the same
        // rule the run used to mark it (pickPhoto); a task no photo shows gets none. Items come newest first,
        // so on a tie the newest photo wins.
        const answers: {
          [taskId: string]: {
            answer: PhotoCheckItem['tasks'][number];
            index: number;
          }[];
        } = {};
        items.forEach((item, index) =>
          item.tasks.forEach(t => {
            if (t.task_id)
              (answers[t.task_id] ||= []).push({ answer: t, index });
          })
        );
        Object.entries(answers).forEach(([taskId, list]) => {
          const pick = pickPhoto(list, undefined, true);
          if (pick) byTask[taskId] = items[pick.index];
        });
        set({ byTask });
      })
      .catch(e => console.warn('Could not load the run photos', e?.message));
  }
}));
